import { useEffect, useState } from "react";

/**
 * Full-screen "an asset is overriding your view" takeover. Pure client theatre,
 * triggered by App when a moving asset gets close to the viewer. Runs a short
 * scripted sequence, then locks on a privacy-override message until the viewer
 * clicks to disable their visibility (or App auto-ends it when the asset leaves).
 */
export const ProximityTakeover = ({ code, signal = 1, onDisable, onRequest }) => {
  const [phase, setPhase] = useState("detect"); // detect -> request -> locked
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("request"), 1700);
    const t2 = setTimeout(() => setPhase("locked"), 4700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    if (phase !== "request") return undefined;
    if (onRequest) onRequest();               // App plays the chime here
    const start = Date.now();
    const id = setInterval(() => {
      setProgress(Math.min(100, ((Date.now() - start) / 2900) * 100));
    }, 55);
    return () => clearInterval(id);
  }, [phase, onRequest]);

  return (
    <div className="prox-overlay" data-testid="proximity-takeover">
      <div className="prox-vignette" />
      <div className="prox-content">
        {phase === "detect" && (
          <>
            <div className="prox-spinner" />
            <div className="prox-nearby">{code} IS NEARBY</div>
            <div className="prox-sub">proximity signal detected</div>
          </>
        )}

        {phase === "request" && (
          <>
            <div className="prox-title">{code}</div>
            <div className="prox-line">has sent a proximity-based connection request</div>
            <div className="prox-bar"><div className="prox-bar-fill" style={{ width: `${progress}%` }} /></div>
            <div className="prox-sub blink">asset is overriding your view…</div>
          </>
        )}

        {phase === "locked" && (
          <>
            <div className="prox-lock-badge">◎ VIEW OVERRIDDEN</div>
            <div className="prox-signal-row">
              <span className="prox-signal-label">LINK</span>
              <div className="prox-signal-track">
                <div
                  className={`prox-signal-fill ${signal <= 0.3 ? "weak" : ""}`}
                  style={{ width: `${Math.round(Math.max(0, Math.min(1, signal)) * 100)}%` }}
                />
              </div>
            </div>
            <div className="prox-locked-msg">
              Privacy mode has been switched on for your viewing session while{" "}
              <strong>{code}</strong> is in the area. You will be able to view the map
              once the asset turns off proximity-based privacy, or manually enables your view.
            </div>
            <button className="prox-disable" onClick={onDisable} data-testid="proximity-disable">
              Click or tap here to disable your visibility to {code}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
